import { RiskIQAccess } from 'types/graphql-global-types';

export interface StudyOption {
    id: string;
    studyNumber: string;
}

// studyId → access level
export type AccessMap = Map<string, RiskIQAccess>;

export const MIXED = 'MIXED' as const;

export const sortStudiesNaturally = (studies: StudyOption[]): StudyOption[] =>
    [...studies].sort((a, b) =>
        a.studyNumber.localeCompare(b.studyNumber, undefined, { numeric: true, sensitivity: 'base' }),
    );

export const filterStudies = (studies: StudyOption[], search: string): StudyOption[] => {
    const term = search.trim().toUpperCase();
    if (!term) return studies;
    return studies.filter((study) => study.studyNumber.toUpperCase().includes(term));
};

export const buildOriginalAccess = (
    studies: StudyOption[],
    members: { studyId: string; riskIQ: RiskIQAccess | null }[],
): AccessMap => {
    const original: AccessMap = new Map(studies.map((study) => [study.id, RiskIQAccess.NO_ACCESS]));
    members.forEach((member) => {
        if (original.has(member.studyId)) {
            original.set(member.studyId, member.riskIQ ?? RiskIQAccess.NO_ACCESS);
        }
    });
    return original;
};

export const currentAccess = (studyId: string, original: AccessMap, edits: AccessMap): RiskIQAccess =>
    edits.get(studyId) ?? original.get(studyId) ?? RiskIQAccess.NO_ACCESS;

export const setAccess = (
    edits: AccessMap,
    original: AccessMap,
    studyIds: string[],
    level: RiskIQAccess,
): AccessMap => {
    const next: AccessMap = new Map(edits);
    studyIds.forEach((studyId) => {
        if (original.get(studyId) === level) next.delete(studyId);
        else next.set(studyId, level);
    });
    return next;
};

export const aggregateAccess = (
    studyIds: string[],
    original: AccessMap,
    edits: AccessMap,
): RiskIQAccess | typeof MIXED | null => {
    if (!studyIds.length) return null;
    const first = currentAccess(studyIds[0], original, edits);
    return studyIds.every((studyId) => currentAccess(studyId, original, edits) === first) ? first : MIXED;
};

export const toMutationInput = (edits: AccessMap): { studyId: string; riskIQ: RiskIQAccess }[] =>
    [...edits.entries()].map(([studyId, riskIQ]) => ({ studyId, riskIQ }));
